import { cn } from '@neo/lib/utils'
import { Input } from '@neo/primitives/input'
import { Lock } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * One copy string for every surface that would otherwise show an auth token. Kept
 * permission-neutral: the same message covers a Plivo support session and a customer role
 * without `api_credentials view`, and it never names an internal profile.
 */
export const TOKEN_ACCESS_RESTRICTED_MESSAGE = 'Restricted for your role'

interface RestrictedTokenNoticeProps {
  testId: string
  className?: string
}

/**
 * Inline lock + copy for places where the token would sit as plain text (table cells,
 * summary pills). For a labelled form field use `RestrictedTokenField` instead.
 */
export const RestrictedTokenNotice = ({ testId, className }: RestrictedTokenNoticeProps) => (
  <span
    className={cn('flex items-center gap-1.5 text-p-400 text-tertiary', className)}
    data-testid={testId}
  >
    <Lock className="h-4 w-4 shrink-0" />
    {TOKEN_ACCESS_RESTRICTED_MESSAGE}
  </span>
)

interface RestrictedTokenFieldProps {
  label: ReactNode
  testId: string
}

/**
 * The locked state of a token input. Same disabled `Input` shell as the Auth ID field beside
 * it, so the two rows line up; the notice is the value and the lock takes the icon slot.
 */
export const RestrictedTokenField = ({ label, testId }: RestrictedTokenFieldProps) => (
  <Input
    label={label}
    value={TOKEN_ACCESS_RESTRICTED_MESSAGE}
    startIcon={Lock}
    disabled
    readOnly
    data-testid={testId}
  />
)
